import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SchedulingMarginKey = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('scheduling_margin_keys');
  const [viewItem, setViewItem] = useState(null);
  const [schedulingMarginKeys, setSchedulingMarginKeys] = useState([]);
  const [plants, setPlants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    loadSchedulingMarginKeys();
    loadPlants();
  }, []);

  const loadSchedulingMarginKeys = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scheduling-margin-keys`);
      setSchedulingMarginKeys(response.data || []);
    } catch (error) {
      console.error('Error loading scheduling margin keys:', error);
      toast.error('Failed to load scheduling margin keys');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-management/plants`);
      setPlants(response.data || []);
    } catch (error) {
      console.error('Error loading plants:', error);
      toast.error('Failed to load plants');
    }
  };

  const handleOpenDialog = (key = null) => {
    setEditingKey(key);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      scheduling_margin_key_id: (formData.get('scheduling_margin_key_id') || '').toUpperCase(),
      plant_id: formData.get('plant_id'),
      description: formData.get('description')
    };

    try {
      if (editingKey) {
        await axios.put(`${BACKEND_URL}/api/scheduling-margin-keys/${editingKey.scheduling_margin_key_id}`, {
          plant_id: data.plant_id,
          description: data.description
        });
        toast.success('Scheduling Margin Key updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/scheduling-margin-keys`, data);
        toast.success('Scheduling Margin Key created successfully');
      }
      setIsDialogOpen(false);
      loadSchedulingMarginKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scheduling margin key');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scheduling Margin Keys</h1>
            <p className="text-sm text-gray-500 mt-1">Manage scheduling margin key configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Scheduling Margin Keys</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Scheduling Margin Key
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Key ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedulingMarginKeys.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-500">
                        No scheduling margin keys found. Click &quot;Add Scheduling Margin Key&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    schedulingMarginKeys.map((key) => {
                      const plant = plants.find(p => p.plant_id === key.plant_id);
                      return (
                        <tr key={key.scheduling_margin_key_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm font-semibold">{key.scheduling_margin_key_id}</td>
                          <td className="py-3 px-4 text-sm">{plant ? `${plant.plant_id} - ${plant.plant_name}` : key.plant_id}</td>
                          <td className="py-3 px-4 text-sm">{key.description}</td>
                          <td className="py-3 px-4 text-right">
                            <RecordActions doctype="scheduling_margin_keys" onView={() => setViewItem(key)} onEdit={() => handleOpenDialog(key)} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingKey ? 'Edit Scheduling Margin Key' : 'Add Scheduling Margin Key'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="scheduling_margin_key_id">Scheduling Margin Key ID * (3 characters)</Label>
                  <Input
                    id="scheduling_margin_key_id"
                    name="scheduling_margin_key_id"
                    defaultValue={editingKey?.scheduling_margin_key_id || ''}
                    placeholder="e.g., 001, 002, 010"
                    maxLength={3}
                    minLength={3}
                    pattern="[0-9]{3}"
                    title="Must be exactly 3 digits"
                    disabled={!!editingKey}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be exactly 3 digits</p>
                </div>
                <div>
                  <Label>Plant *</Label>
                  <select
                    name="plant_id"
                    defaultValue={editingKey?.plant_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Plant</option>
                    {plants.map(plant => (
                      <option key={plant.plant_id} value={plant.plant_id}>
                        {plant.plant_id} - {plant.plant_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Input
                  name="description"
                  defaultValue={editingKey?.description || ''}
                  placeholder="e.g., Standard Scheduling Margin"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingKey ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default SchedulingMarginKey;
